// ═══════════════════════════════════════════════════════════════════
//  modules/projets/ProjectChat.js
//  ProjectChat
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "../../composants/Badge";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { CATEGORY_LABELS, PROJECT_PRIORITY_COLORS } from "./constantes";

export function ProjectChat({ token, projectId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [editedTasks, setEditedTasks] = useState([]);
  const [applying, setApplying] = useState(false);
  const messagesContainerRef = useRef(null);
  const prevMessagesCount = useRef(0);

  const loadMessages = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/chat`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setMessages(Array.isArray(data) ? data : []);
  }, [projectId, token]);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 4000);
    return () => clearInterval(interval);
  }, [projectId, loadMessages]);

  useEffect(() => {
    if (messages.length > prevMessagesCount.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    prevMessagesCount.current = messages.length;
  }, [messages, loadMessages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    setSending(true);
    await fetch(`${API_BASE}/api/projects/${projectId}/chat`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: input }),
    });
    setInput(""); setSending(false); loadMessages();
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/analyze`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) { alert(data.error || "Erreur lors de l'analyse"); setAnalyzing(false); return; }
      setProposal(data.proposal);
      setEditedTasks(data.proposal.tasks.map((t, i) => ({ ...t, _id: i, _accepted: true })));
    } catch { alert("Impossible de contacter le serveur"); }
    setAnalyzing(false);
  };

  const applyProposal = async () => {
    setApplying(true);
    try {
      await fetch(`${API_BASE}/api/projects/${projectId}/analyze/apply`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tasks: editedTasks.filter((t) => t._accepted) }),
      });
      setProposal(null); setEditedTasks([]); loadMessages();
    } catch { alert("Erreur lors de l'application"); }
    setApplying(false);
  };

  const updateEditedTask = (id, field, value) => setEditedTasks((prev) => prev.map((t) => t._id === id ? { ...t, [field]: value } : t));

  if (proposal) {
    return (
      <div style={{ background: THEME.panel, border: `1px solid ${THEME.purple}`, borderRadius: 10, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ color: THEME.purple, fontSize: 14, fontWeight: 700 }}>🤖 Proposition de l'IA</div>
            <div style={{ color: THEME.textMuted, fontSize: 12, marginTop: 2 }}>{editedTasks.length} tâche(s) proposée(s) — accepte, modifie ou rejette chacune</div>
          </div>
          <button onClick={() => { setProposal(null); setEditedTasks([]); }} style={{ background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}>✕ Annuler</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 420, overflowY: "auto" }}>
          {editedTasks.map((t) => {
            const catInfo = CATEGORY_LABELS[t.category] || { label: t.category, color: THEME.textMuted, bg: THEME.surface };
            return (
              <div key={t._id} style={{ background: THEME.surface, border: `1px solid ${t._accepted ? catInfo.color : THEME.border}`, borderRadius: 8, padding: 12, opacity: t._accepted ? 1 : 0.4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <Badge text={t.action === "create" ? "✨ Nouvelle" : "🔄 Enrichir"} bg={t.action === "create" ? THEME.emeraldDim : THEME.accentDim} color={t.action === "create" ? THEME.emerald : THEME.accent} />
                    <Badge text={catInfo.label} bg={catInfo.bg} color={catInfo.color} />
                    {t.priority && <Badge text={t.priority} bg={PROJECT_PRIORITY_COLORS[t.priority]?.bg} color={PROJECT_PRIORITY_COLORS[t.priority]?.text} />}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => updateEditedTask(t._id, "_accepted", true)} style={{ background: t._accepted ? THEME.emeraldDim : "transparent", border: `1px solid ${THEME.emerald}`, color: THEME.emerald, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>✅</button>
                    <button onClick={() => updateEditedTask(t._id, "_accepted", false)} style={{ background: !t._accepted ? THEME.redDim : "transparent", border: `1px solid ${THEME.red}`, color: THEME.red, borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>❌</button>
                  </div>
                </div>
                <input value={t.title} onChange={(e) => updateEditedTask(t._id, "title", e.target.value)}
                  style={{ width: "100%", background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "6px 10px", color: THEME.text, fontSize: 13, fontWeight: 600, outline: "none", marginBottom: 6, boxSizing: "border-box" }} />
                <textarea value={t.description || ""} onChange={(e) => updateEditedTask(t._id, "description", e.target.value)} rows={2}
                  style={{ width: "100%", background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "6px 10px", color: THEME.textMuted, fontSize: 12, outline: "none", resize: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                {Array.isArray(t.steps) && t.steps.length > 0 && <div style={{ marginTop: 6 }}>{t.steps.map((s, si) => <div key={si} style={{ color: THEME.textMuted, fontSize: 11, padding: "2px 0" }}>· {s}</div>)}</div>}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => { setProposal(null); setEditedTasks([]); }} style={{ flex: 1, background: "transparent", border: `1px solid ${THEME.border}`, color: THEME.textMuted, borderRadius: 8, padding: "10px", cursor: "pointer", fontSize: 13 }}>Annuler</button>
          <button onClick={applyProposal} disabled={applying || editedTasks.filter((t) => t._accepted).length === 0}
            style={{ flex: 2, background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, color: THEME.purple, borderRadius: 8, padding: "10px", cursor: "pointer", fontSize: 13, fontWeight: 700, opacity: applying ? 0.6 : 1 }}>
            {applying ? "Application en cours..." : `✅ Appliquer (${editedTasks.filter((t) => t._accepted).length} tâche(s))`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 20, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ color: THEME.text, fontSize: 14, fontWeight: 700, margin: 0 }}>💬 Chat du projet</h3>
        <button onClick={analyze} disabled={analyzing}
          style={{ background: THEME.purpleDim, border: `1px solid ${THEME.purple}`, color: THEME.purple, borderRadius: 6, padding: "6px 14px", cursor: analyzing ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: analyzing ? 0.6 : 1 }}>
          {analyzing ? "⏳ Analyse en cours..." : "🤖 Analyser la conversation"}
        </button>
      </div>
      <div ref={messagesContainerRef} style={{ background: "#0D1117", border: `1px solid ${THEME.border}`, borderRadius: 8, padding: 14, height: 380, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
        {messages.length === 0 && <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucun message pour l'instant. Lance la discussion !</div>}
        {messages.map((m) => {
          const isMine = m.author_id === currentUserId;
          return (
            <div key={m.id} style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth: "75%", background: isMine ? THEME.accentDim : THEME.surface, border: `1px solid ${m.consumed ? THEME.textDim : isMine ? THEME.accent : THEME.border}`, borderRadius: 8, padding: "8px 12px", opacity: m.consumed ? 0.5 : 1 }}>
              <div style={{ color: isMine ? THEME.accent : THEME.gold, fontWeight: 700, fontSize: 11, marginBottom: 3 }}>{m.author_username || "Staff"} {m.consumed && <span style={{ color: THEME.textDim, fontWeight: 400 }}>· analysé</span>}</div>
              <div style={{ color: THEME.text, fontSize: 13 }}>{m.content}</div>
              <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 3 }}>{new Date(m.created_at).toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Propose une idée, discute d'une tâche..."
          style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "10px 14px", color: THEME.text, fontSize: 13, outline: "none" }} />
        <button onClick={sendMessage} disabled={sending} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "10px 20px", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Envoyer</button>
      </div>
    </div>
  );
}
